import { c as _c } from "react/compiler-runtime";
import { addSeconds, endOfDay, startOfDay, sub } from 'date-fns';
import { Fragment, useContext, useRef } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import useSWRInfinite from 'swr/infinite';
import { APIList } from '../../../../../../../../typings/API.interface';
import { APIDestination } from '../../../../../../../../typings/Destination.interface';
import { Issue, IssueStatus, IssueType } from '../../../../../../../../typings/Issue.interface';
import { APISource } from '../../../../../../../../typings/Source.interface';
import { Transformation } from '../../../../../../../../typings/Transformation.interface';
import { APIWebhook } from '../../../../../../../../typings/Webhook.interface';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import formatMetricsQuery from '../../../../../utils/formatMetricsQuery';
import { issue_actions } from '../../../../../utils/issues';
import Button from '../../../../common/base/Button';
import Divider from '../../../../common/base/Divider';
import Loading from '../../../../common/base/Loading';
import Skeleton from '../../../../common/base/Skeleton';
import { EmptyResults, StyledEmptyStateWrapper } from '../../../../common/EmptyState';
import { Div } from '../../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import useMetricsQuery from '../../../../hooks/useMetricsQuery';
import { DashboardContext } from '../../DashboardContext';
import IssueSection from './IssueSection';
export interface IssuesRenderData {
  transformations_by_id: Record<string, Transformation> | undefined;
  webhooks_by_id: Record<string, APIWebhook> | undefined;
  destinations_by_id: Record<string, APIDestination> | undefined;
  sources_by_id: Record<string, APISource> | undefined;
  timeseries_by_issue_id?: Record<string, [Date, number][]>;
}

/** Returns the previous array while `new_values` is a subset of it, so a shrinking result
 * set keeps the larger already-fetched value (and its serialized SWR keys) instead of
 * refetching. The render-time ref read/write is intentional previous-render state;
 * 'use no memo' quarantines the rule violation so React Compiler still compiles callers. */
const useSubsetStableArray = <T,>(new_values: T[]): T[] => {
  'use no memo';

  const prev_ref = useRef<T[]>([]);
  const old_set = new Set(prev_ref.current);
  if (new_values.every(value => old_set.has(value))) {
    return prev_ref.current;
  }
  prev_ref.current = new_values;
  return new_values;
};

/** Same subset-stabilization as useSubsetStableArray, comparing issues by id. */
const useSubsetStableIssues = (issues: Issue[]): Issue[] => {
  'use no memo';

  const prev_ref = useRef<Issue[]>([]);
  const old_ids = new Set(prev_ref.current.map(issue => issue.id));
  if (issues.every(issue_0 => old_ids.has(issue_0.id))) {
    return prev_ref.current;
  }
  prev_ref.current = issues;
  return issues;
};
const IssueListSkeleton: React.FC = () => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      x: 6,
      y: 4
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Skeleton w={{
      px: 200
    }} h={{
      px: 20
    }} border_radius="normal" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      px: 400
    };
    t3 = {
      px: 20
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Div><Div p={t0}>{t1}<Skeleton w={t2} h={t3} border_radius="normal" m={{
          t: 1
        }} /></Div><Divider /></Div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
export const useIssuesList = (types, status, sort_by, issue_trigger_id) => {
  const $ = _c(73);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    mutate
  } = useSWRConfig();
  const {
    team
  } = useContext(DashboardContext);
  const t0 = issue_trigger_id || undefined;
  let t1;
  if ($[0] !== sort_by || $[1] !== status || $[2] !== t0) {
    t1 = {
      order_by: sort_by,
      dir: "desc",
      limit: 50,
      status,
      issue_trigger_id: t0
    };
    $[0] = sort_by;
    $[1] = status;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const issues_pagination = t1;
  let t2;
  if ($[4] !== issues_pagination || $[5] !== types) {
    t2 = !types ? issues_pagination : {
      type: types,
      ...issues_pagination
    };
    $[4] = issues_pagination;
    $[5] = types;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const issue_filters = t2;
  let t3;
  if ($[7] !== issue_filters) {
    t3 = (_, previous_data) => APIMethodKeys.issues.list({
      ...issue_filters,
      next: previous_data?.pagination.next
    });
    $[7] = issue_filters;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== HookdeckAPI) {
    t4 = t5 => {
      const [url] = t5;
      return HookdeckAPI.request("GET", url);
    };
    $[9] = HookdeckAPI;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== mutate || $[12] !== team) {
    t5 = {
      onSuccess: () => mutate([APIMethodKeys.issues.count({
        status: "OPENED"
      }), team.id])
    };
    $[11] = mutate;
    $[12] = team;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const {
    data: issues_pages,
    mutate: mutateIssuesPages,
    size,
    setSize,
    isValidating
  } = useSWRInfinite(t3, t4, t5);
  const issues = issues_pages?.reduce(_temp, []) ?? [];
  const unique_webhook_ids = useSubsetStableArray(Array.from(new Set(issues.reduce(_temp2, []))));
  const unique_destination_ids = useSubsetStableArray(Array.from(new Set(issues.reduce(_temp3, []))));
  const unique_transformation_ids = useSubsetStableArray(Array.from(new Set(issues.reduce(_temp4, []))));
  const unique_source_ids = useSubsetStableArray(Array.from(new Set(issues.reduce(_temp5, []))));
  const unique_issues_for_metrics = useSubsetStableIssues(issues);
  let t6;
  if ($[14] !== unique_issues_for_metrics) {
    t6 = unique_issues_for_metrics?.some(_temp6);
    $[14] = unique_issues_for_metrics;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const has_delivery_issues = t6;
  let t7;
  if ($[16] !== has_delivery_issues || $[17] !== unique_issues_for_metrics) {
    const query_params = {
      filters: {
        issue_id: unique_issues_for_metrics?.map(_temp7),
        date: {
          min: startOfDay(sub(new Date(), {
            days: 14
          })).toISOString(),
          max: endOfDay(new Date()).toISOString()
        }
      },
      granularity: "hour" as const,
      dimensions: ["issue_id"]
    };
    t7 = unique_issues_for_metrics && has_delivery_issues ? formatMetricsQuery("EventsWithIssues", query_params) : null;
    $[16] = has_delivery_issues;
    $[17] = unique_issues_for_metrics;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const metrics_query_delivery = t7;
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      resetResultSetOnChange: false
    };
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const {
    raw_data: raw_data_delivery
  } = useMetricsQuery(metrics_query_delivery, t8);
  let t9;
  if ($[20] !== unique_issues_for_metrics) {
    t9 = unique_issues_for_metrics?.some(_temp8);
    $[20] = unique_issues_for_metrics;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const has_transformation_issues = t9;
  let t10;
  if ($[22] !== has_transformation_issues || $[23] !== unique_issues_for_metrics) {
    const query_params_0 = {
      filters: {
        issue_id: unique_issues_for_metrics?.map(_temp9),
        date: {
          min: startOfDay(sub(new Date(), {
            days: 14
          })).toISOString(),
          max: addSeconds(endOfDay(new Date()), 1).toISOString()
        }
      },
      granularity: "hour" as const,
      dimensions: ["issue_id"]
    };
    t10 = unique_issues_for_metrics && has_transformation_issues ? formatMetricsQuery("TransformationExecutions", query_params_0) : null;
    $[22] = has_transformation_issues;
    $[23] = unique_issues_for_metrics;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const metrics_query_transformation = t10;
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      resetResultSetOnChange: false
    };
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const {
    raw_data: raw_data_transformation
  } = useMetricsQuery(metrics_query_transformation, t11);
  let t12;
  bb0: {
    if (!issues || metrics_query_delivery && !raw_data_delivery || metrics_query_transformation && !raw_data_transformation) {
      t12 = undefined;
      break bb0;
    }
    let t13;
    if ($[26] !== issues || $[27] !== raw_data_delivery || $[28] !== raw_data_transformation) {
      const now = new Date();
      const all_data = [...(raw_data_delivery ? raw_data_delivery.map(_temp10) : []), ...(raw_data_transformation ? raw_data_transformation.map(_temp11) : [])];
      const data_index = {};
      all_data.forEach(entry_1 => {
        const day_key = startOfDay(entry_1.created_at).toISOString();
        if (!data_index[entry_1.issue_id]) {
          data_index[entry_1.issue_id] = {};
        }
        if (!data_index[entry_1.issue_id][day_key]) {
          data_index[entry_1.issue_id][day_key] = 0;
        }
        data_index[entry_1.issue_id][day_key] = data_index[entry_1.issue_id][day_key] + entry_1.count;
      });
      t13 = issues.reduce((obj, t14) => {
        const {
          id
        } = t14;
        obj[id] = Array.from(Array(14).keys()).map(i => {
          const step_start = startOfDay(sub(now, {
            days: i
          }));
          const day_key_0 = step_start.toISOString();
          const count = data_index[id]?.[day_key_0] || 0;
          return [step_start, count];
        }).reverse();
        return obj;
      }, {});
      $[26] = issues;
      $[27] = raw_data_delivery;
      $[28] = raw_data_transformation;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    t12 = t13;
  }
  const timeseries_by_issue_id = t12;
  let t13;
  if ($[30] !== unique_webhook_ids) {
    t13 = unique_webhook_ids.length > 0 && APIMethodKeys.webhooks.list({
      id: unique_webhook_ids
    });
    $[30] = unique_webhook_ids;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== HookdeckAPI.webhooks || $[33] !== unique_webhook_ids) {
    t14 = () => HookdeckAPI.webhooks.list({
      id: unique_webhook_ids
    });
    $[32] = HookdeckAPI.webhooks;
    $[33] = unique_webhook_ids;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const {
    data: webhooks
  } = useSWR(t13, t14);
  let t15;
  if ($[35] !== unique_destination_ids) {
    t15 = unique_destination_ids.length > 0 && APIMethodKeys.destinations.list({
      id: unique_destination_ids
    });
    $[35] = unique_destination_ids;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== HookdeckAPI.destinations || $[38] !== unique_destination_ids) {
    t16 = () => HookdeckAPI.destinations.list({
      id: unique_destination_ids
    });
    $[37] = HookdeckAPI.destinations;
    $[38] = unique_destination_ids;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const {
    data: destinations
  } = useSWR(t15, t16);
  let t17;
  if ($[40] !== unique_transformation_ids) {
    t17 = unique_transformation_ids.length > 0 && APIMethodKeys.transformations.list({
      id: unique_transformation_ids
    });
    $[40] = unique_transformation_ids;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== HookdeckAPI.transformations || $[43] !== unique_transformation_ids) {
    t18 = () => HookdeckAPI.transformations.list({
      id: unique_transformation_ids
    });
    $[42] = HookdeckAPI.transformations;
    $[43] = unique_transformation_ids;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const {
    data: transformations
  } = useSWR(t17, t18);
  let t19;
  if ($[45] !== unique_source_ids) {
    t19 = unique_source_ids.length > 0 && APIMethodKeys.sources.list({
      id: unique_source_ids
    });
    $[45] = unique_source_ids;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== HookdeckAPI.sources || $[48] !== unique_source_ids) {
    t20 = () => HookdeckAPI.sources.list({
      id: unique_source_ids
    });
    $[47] = HookdeckAPI.sources;
    $[48] = unique_source_ids;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  const {
    data: sources
  } = useSWR(t19, t20);
  let t21;
  if ($[50] !== webhooks?.models) {
    t21 = webhooks?.models.reduce(_temp12, {});
    $[50] = webhooks?.models;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  const webhooks_by_id = t21;
  let t22;
  if ($[52] !== destinations?.models) {
    t22 = destinations?.models.reduce(_temp13, {});
    $[52] = destinations?.models;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  const destinations_by_id = t22;
  let t23;
  if ($[54] !== transformations?.models) {
    t23 = transformations?.models.reduce(_temp14, {});
    $[54] = transformations?.models;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  const transformations_by_id = t23;
  let t24;
  if ($[56] !== sources?.models) {
    t24 = sources?.models.reduce(_temp15, {});
    $[56] = sources?.models;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  const sources_by_id = t24;
  const t25 = timeseries_by_issue_id || undefined;
  let t26;
  if ($[58] !== destinations_by_id || $[59] !== sources_by_id || $[60] !== t25 || $[61] !== transformations_by_id || $[62] !== webhooks_by_id) {
    t26 = {
      webhooks_by_id,
      transformations_by_id,
      timeseries_by_issue_id: t25,
      destinations_by_id,
      sources_by_id
    };
    $[58] = destinations_by_id;
    $[59] = sources_by_id;
    $[60] = t25;
    $[61] = transformations_by_id;
    $[62] = webhooks_by_id;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  const render_data = t26;
  const loading = !issues_pages || unique_webhook_ids.length > 0 && !webhooks || unique_destination_ids.length > 0 && !destinations || unique_transformation_ids.length > 0 && !transformations || unique_source_ids.length > 0 && !sources;
  let t27;
  if ($[64] !== isValidating || $[65] !== issues || $[66] !== issues_pages || $[67] !== loading || $[68] !== mutateIssuesPages || $[69] !== render_data || $[70] !== setSize || $[71] !== size) {
    t27 = {
      issues,
      issues_pages,
      mutateIssuesPages,
      size,
      setSize,
      isValidating,
      render_data,
      loading
    };
    $[64] = isValidating;
    $[65] = issues;
    $[66] = issues_pages;
    $[67] = loading;
    $[68] = mutateIssuesPages;
    $[69] = render_data;
    $[70] = setSize;
    $[71] = size;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  return t27;
};
const IssuesList: React.FC<{
  types?: IssueType[];
  hide_header?: boolean;
  hide_empty_groups?: boolean;
  group_by?: 'type' | 'status';
  sort_by?: 'last_seen_at' | 'first_seen_at';
  issue_trigger_id?: string;
  status?: IssueStatus;
  applyAction: (issues_id: string[], action: keyof typeof issue_actions) => void;
}> = t0 => {
  const $ = _c(15);
  const {
    types,
    status,
    sort_by,
    issue_trigger_id,
    applyAction
  } = t0;
  const {
    issues,
    issues_pages,
    size,
    setSize,
    isValidating,
    render_data,
    loading
  } = useIssuesList(types, status, sort_by, issue_trigger_id);
  if (loading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <IssueListSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== applyAction || $[2] !== issues || $[3] !== render_data) {
    t1 = issues.length ? issues.map(issue => <Fragment key={issue.id}><IssueSection key={issue.id} issue={issue} render_data={render_data} applyAction={applyAction} /><Divider /></Fragment>) : <StyledEmptyStateWrapper><EmptyResults label="No issues found" /></StyledEmptyStateWrapper>;
    $[1] = applyAction;
    $[2] = issues;
    $[3] = render_data;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== issues_pages || $[6] !== setSize || $[7] !== size) {
    t2 = issues_pages?.[issues_pages.length - 1].pagination.next && <Div flex={{
      justify: "center"
    }} m={{
      y: 8
    }}><Button icon="history" onClick={() => setSize(size + 1)} ghost={true} primary={true}>View more issues</Button></Div>;
    $[5] = issues_pages;
    $[6] = setSize;
    $[7] = size;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== isValidating) {
    t3 = isValidating && <Div flex={{
      justify: "center"
    }} p={2} m={{
      b: 8
    }}><Loading /></Div>;
    $[9] = isValidating;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
export default IssuesList;
function _temp(arr, page) {
  return [...arr, ...page.models];
}
function _temp2(arr_0, issue) {
  return issue.type === "delivery" ? [...arr_0, ...issue.aggregation_keys.webhook_id] : arr_0;
}
function _temp3(arr_1, issue_0) {
  return issue_0.type === "backpressure" ? [...arr_1, ...issue_0.aggregation_keys.destination_id] : arr_1;
}
function _temp4(arr_2, issue_1) {
  return issue_1.type === "transformation" ? [...arr_2, ...issue_1.aggregation_keys.transformation_id] : arr_2;
}
function _temp5(arr_3, issue_2) {
  return issue_2.type === "request" ? [...arr_3, ...issue_2.aggregation_keys.source_id] : arr_3;
}
function _temp6(iss) {
  return iss.type === "delivery";
}
function _temp7(issue_3) {
  return issue_3.id;
}
function _temp8(iss_0) {
  return iss_0.type === "transformation";
}
function _temp9(issue_4) {
  return issue_4.id;
}
function _temp10(entry) {
  return {
    created_at: new Date(entry["EventsWithIssues.createdAt"]),
    count: parseInt(entry["EventsWithIssues.count"]),
    issue_id: entry["EventsWithIssues.issue_id"]
  };
}
function _temp11(entry_0) {
  return {
    created_at: new Date(entry_0["TransformationExecutions.createdAt"]),
    count: parseInt(entry_0["TransformationExecutions.count"]),
    issue_id: entry_0["TransformationExecutions.issue_id"]
  };
}
function _temp12(object, webhook) {
  return {
    ...object,
    [webhook.id]: webhook
  };
}
function _temp13(object_0, destination) {
  return {
    ...object_0,
    [destination.id]: destination
  };
}
function _temp14(object_1, transformation) {
  return {
    ...object_1,
    [transformation.id]: transformation
  };
}
function _temp15(object_2, source) {
  return {
    ...object_2,
    [source.id]: source
  };
}